import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../hooks/use-toast";

// Custom renderers for Fathom summary markdown — defined outside to avoid re-creating on every render
const MD_H1 = (p) => <h1 className="text-xl font-bold text-gray-900 mt-6 mb-3 first:mt-0" style={{fontFamily: "Outfit, sans-serif"}} {...p} />;
const MD_H2 = (p) => <h2 className="text-lg font-bold text-gray-900 mt-5 mb-2 first:mt-0 pb-1 border-b border-gray-200" style={{fontFamily: "Outfit, sans-serif"}} {...p} />;
const MD_H3 = (p) => <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2" style={{fontFamily: "Outfit, sans-serif"}} {...p} />;
const MD_H4 = (p) => <h4 className="text-sm font-semibold text-gray-700 mt-3 mb-1" {...p} />;
const MD_P = (p) => <p className="text-sm text-gray-700 mb-2 leading-relaxed" {...p} />;
const MD_UL = (p) => <ul className="list-disc pl-5 space-y-1 mb-3 text-sm text-gray-700" {...p} />;
const MD_OL = (p) => <ol className="list-decimal pl-5 space-y-1 mb-3 text-sm text-gray-700" {...p} />;
const MD_LI = (p) => <li className="leading-relaxed" {...p} />;
const MD_STRONG = (p) => <strong className="font-semibold text-gray-900" {...p} />;
const MD_EM = (p) => <em className="italic" {...p} />;
const MD_A = (p) => <a target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline decoration-blue-200 hover:decoration-blue-500 transition-colors" {...p} />;
const MD_BLOCKQUOTE = (p) => <blockquote className="border-l-4 border-gray-300 pl-3 italic text-gray-600 my-2" {...p} />;
const MD_HR = () => <hr className="my-4 border-gray-200" />;
const MD_CODE = ({inline, ...p}) => inline
  ? <code className="bg-gray-100 text-gray-800 rounded px-1 py-0.5 text-xs font-mono" {...p} />
  : <code className="block bg-gray-100 text-gray-800 rounded p-2 text-xs font-mono overflow-x-auto" {...p} />;

const SUMMARY_MD_COMPONENTS = {
  h1: MD_H1, h2: MD_H2, h3: MD_H3, h4: MD_H4,
  p: MD_P, ul: MD_UL, ol: MD_OL, li: MD_LI,
  strong: MD_STRONG, em: MD_EM, a: MD_A,
  blockquote: MD_BLOCKQUOTE, hr: MD_HR, code: MD_CODE,
};

const SUMMARY_MD_PLUGINS = [remarkGfm];

export default function CoachingSessionsView({ studentId = null, isCoachView = false }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [students, setStudents] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignEmail, setAssignEmail] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ student_email: "", title: "", duration_minutes: "", video_url: "", transcript: "" });
  const { toast } = useToast();

  const canManage = isCoachView && (user?.role === "coach" || user?.role === "admin");

  useEffect(() => {
    fetchSessions();
    if (isCoachView && (user?.role === "coach" || user?.role === "admin")) {
      api.get("/coach/students").then(res => {
        setStudents(res.data || []);
        if (studentId) {
          const st = (res.data || []).find(s => s.id === studentId);
          if (st) setForm(f => ({ ...f, student_email: st.email }));
        }
      }).catch(() => {});
    }
  }, [studentId]);

  const fetchSessions = async () => {
    try {
      const url = studentId ? `/coaching-sessions?student_id=${studentId}` : "/coaching-sessions";
      const res = await api.get(url);
      setSessions(res.data);
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de charger les sessions" });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSession = async () => {
    if (!form.student_email) {
      toast({ variant: "destructive", title: "Sélectionnez un élève" });
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/coaching-sessions/manual", {
        student_email: form.student_email,
        title: form.title || "Session de coaching",
        duration_minutes: parseInt(form.duration_minutes) || 0,
        video_url: form.video_url,
        transcript: form.transcript,
      }, { timeout: 180000 });
      const matched = res.data?.fathom_matched;
      const hasSummary = res.data?.has_summary;
      const hasTranscript = res.data?.has_transcript;
      const reason = res.data?.enrichment_reason;
      if (matched && hasSummary && hasTranscript) {
        toast({ title: "Session ajoutée ✨", description: "Résumé et transcription Fathom récupérés automatiquement." });
      } else if (matched) {
        toast({ title: "Session ajoutée", description: "Lien Fathom détecté, mais résumé/transcription encore indisponibles côté Fathom." });
      } else if (reason) {
        toast({ title: "Session ajoutée (sans enrichissement)", description: reason });
      } else {
        toast({ title: "Session ajoutée !" });
      }
      setShowAddModal(false);
      setForm({ student_email: "", title: "", duration_minutes: "", video_url: "", transcript: "" });
      fetchSessions();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Erreur" });
    } finally {
      setSaving(false);
    }
  };

  const handleSyncFathom = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/coaching-sessions/sync-fathom", null, { timeout: 600000 });
      const { imported, matched, pending, skipped_existing } = res.data;
      toast({
        title: `Synchronisation Fathom terminée`,
        description: `${imported} importée${imported > 1 ? "s" : ""} (${matched} reconnue${matched > 1 ? "s" : ""}, ${pending} à assigner), ${skipped_existing} déjà présente${skipped_existing > 1 ? "s" : ""}`,
      });
      fetchSessions();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur de synchronisation", description: e.response?.data?.detail || "Erreur" });
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteSession = async (session) => {
    if (!window.confirm(`Supprimer la session « ${session.title} » ?`)) return;
    try {
      await api.delete(`/coaching-sessions/${session.id}`);
      toast({ title: "Session supprimée" });
      fetchSessions();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Erreur" });
    }
  };

  const handleSaveTitle = async (sessionId) => {
    const newTitle = (titleDraft || "").trim();
    if (!newTitle) {
      toast({ variant: "destructive", title: "Titre requis" });
      return;
    }
    try {
      await api.patch(`/coaching-sessions/${sessionId}`, { title: newTitle });
      setEditingTitleId(null);
      setTitleDraft("");
      if (selectedSession && selectedSession.id === sessionId) {
        setSelectedSession({ ...selectedSession, title: newTitle });
      }
      toast({ title: "Titre mis à jour" });
      fetchSessions();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Erreur" });
    }
  };

  const handleAssignSession = async (studentEmail) => {
    if (!assignTarget || !studentEmail) return;
    setSaving(true);
    try {
      await api.put(`/coaching-sessions/${assignTarget.id}/assign?student_email=${encodeURIComponent(studentEmail)}`);
      toast({ title: "Session assignée !" });
      setAssignTarget(null);
      setAssignEmail("");
      fetchSessions();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Erreur" });
    } finally {
      setSaving(false);
    }
  };

  const handleViewSession = async (sessionId) => {
    try {
      const res = await api.get(`/coaching-sessions/${sessionId}`);
      setSelectedSession(res.data);
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const handleGenerateSummary = async () => {
    if (!selectedSession) return;
    setGeneratingSummary(true);
    try {
      const res = await api.post(`/coaching-sessions/${selectedSession.id}/generate-summary`, null, { timeout: 180000 });
      setSelectedSession({ ...selectedSession, ai_summary: res.data.summary });
      toast({ title: "Résumé généré !" });
      fetchSessions();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Erreur" });
    } finally {
      setGeneratingSummary(false);
    }
  };

  const [translatingSummary, setTranslatingSummary] = useState(false);
  const handleTranslateToFrench = async () => {
    if (!selectedSession) return;
    setTranslatingSummary(true);
    try {
      const res = await api.post(`/coaching-sessions/${selectedSession.id}/translate-fr`, null, { timeout: 180000 });
      setSelectedSession({ ...selectedSession, ai_summary: res.data.summary });
      toast({
        title: res.data.status === "unchanged" ? "Résumé déjà en français" : "Résumé traduit en français ✓",
      });
      fetchSessions();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e.response?.data?.detail || "Erreur" });
    } finally {
      setTranslatingSummary(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "-";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h${m > 0 ? m : ""}` : `${m} min`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => <div key={i} className="skeleton h-24 rounded-2xl" />)}
      </div>
    );
  }

  // Session detail view
  if (selectedSession) {
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSelectedSession(null)}
            className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div className="flex-1">
            {canManage && editingTitleId === selectedSession.id ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTitle(selectedSession.id); if (e.key === 'Escape') { setEditingTitleId(null); setTitleDraft(''); } }}
                  autoFocus
                  className="text-xl font-bold text-gray-900 bg-white border border-gray-300 rounded-lg px-2 py-1 flex-1"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                  data-testid="session-title-input"
                />
                <button onClick={() => handleSaveTitle(selectedSession.id)} className="btn-primary text-xs px-3 py-1.5" data-testid="save-session-title-btn">OK</button>
                <button onClick={() => { setEditingTitleId(null); setTitleDraft(''); }} className="btn-ghost text-xs px-3 py-1.5">Annuler</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {selectedSession.title}
                </h2>
                {canManage && (
                  <button
                    onClick={() => { setEditingTitleId(selectedSession.id); setTitleDraft(selectedSession.title); }}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                    title="Modifier le titre"
                    data-testid="edit-session-title-btn"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                )}
              </div>
            )}
            <p className="text-sm text-gray-500">
              {formatDate(selectedSession.created_at)} • {formatDuration(selectedSession.duration_minutes)}
            </p>
          </div>
          {selectedSession.video_url && (
            <a
              href={selectedSession.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Voir le replay
            </a>
          )}
        </div>

        {/* AI Summary */}
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <svg className="w-5 h-5 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Résumé IA
            </h3>
            {!selectedSession.ai_summary && selectedSession.has_transcript && (
              <button
                onClick={handleGenerateSummary}
                disabled={generatingSummary}
                className="btn-ghost text-sm flex items-center gap-2"
              >
                {generatingSummary ? (
                  <>
                    <div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Générer le résumé
                  </>
                )}
              </button>
            )}
            {selectedSession.ai_summary && canManage && (
              <button
                onClick={handleTranslateToFrench}
                disabled={translatingSummary}
                className="btn-ghost text-sm flex items-center gap-2"
                title="Retraduire le résumé en français"
                data-testid="translate-summary-fr-btn"
              >
                {translatingSummary ? (
                  <>
                    <div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                    Traduction...
                  </>
                ) : (
                  <>🇫🇷 Traduire en français</>
                )}
              </button>
            )}
          </div>
          {selectedSession.ai_summary ? (
            <div className="fathom-summary text-gray-800 leading-relaxed">
              <ReactMarkdown remarkPlugins={SUMMARY_MD_PLUGINS} components={SUMMARY_MD_COMPONENTS}>
                {selectedSession.ai_summary}
              </ReactMarkdown>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">
              {selectedSession.has_transcript
                ? "Clique sur \"Générer le résumé\" pour obtenir une synthèse de cette session."
                : "Aucun résumé disponible pour l'instant. Si cette session vient de Fathom, relance une synchronisation pour récupérer le résumé Fathom."}
            </p>
          )}
        </div>

        {/* Transcript */}
        {selectedSession.transcript && (
          <div className="glass-card rounded-2xl p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Transcription complète
            </h3>
            <div className="bg-gray-50 rounded-xl p-4 max-h-96 overflow-y-auto">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                {selectedSession.transcript}
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Sessions list view
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            {isCoachView ? "Sessions de coaching" : "Mes sessions"}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {sessions.length} session{sessions.length > 1 ? "s" : ""} enregistrée{sessions.length > 1 ? "s" : ""}
          </p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleSyncFathom}
              disabled={syncing}
              className="btn-ghost flex items-center gap-2 text-sm disabled:opacity-50"
              data-testid="sync-fathom-btn"
            >
              <svg className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {syncing ? "Synchronisation..." : "Synchroniser Fathom"}
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary flex items-center gap-2 text-sm"
              data-testid="add-session-btn"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Ajouter une session
            </button>
          </div>
        )}
      </div>

      {/* Sessions list */}
      {sessions.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-gray-600 font-medium">Aucune session enregistrée</p>
          <p className="text-sm text-gray-500 mt-1">
            Les sessions de coaching avec ton Major apparaîtront ici automatiquement après chaque appel Fathom.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map(session => (
            <div
              key={session.id}
              onClick={() => handleViewSession(session.id)}
              className="glass-card glass-card-hover rounded-2xl p-5 cursor-pointer"
              data-testid={`session-${session.id}`}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  session.has_summary ? "bg-emerald-100" : session.has_transcript ? "bg-blue-100" : "bg-gray-100"
                }`}>
                  {session.has_summary ? (
                    <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : session.has_transcript ? (
                    <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-gray-900 truncate max-w-full">{session.title}</h3>
                    {session.has_summary && (
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg">
                        Résumé dispo
                      </span>
                    )}
                    {canManage && session.status === "pending_match" && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-lg" data-testid={`pending-badge-${session.id}`}>
                        À assigner
                      </span>
                    )}
                    {canManage && session.student_name && (
                      <span className="px-2 py-0.5 bg-[#1a2744]/5 text-[#1a2744] text-xs font-medium rounded-lg" data-testid={`session-student-${session.id}`}>
                        {session.student_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{formatDate(session.created_at)}</span>
                    <span>{formatDuration(session.duration_minutes)}</span>
                    {session.video_url && (
                      <span className="flex items-center gap-1 text-violet-600">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        </svg>
                        Replay
                      </span>
                    )}
                  </div>
                </div>

                {/* Assign button for pending sessions */}
                {canManage && session.status === "pending_match" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setAssignTarget(session); }}
                    className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors flex-shrink-0"
                    data-testid={`assign-session-btn-${session.id}`}
                  >
                    Assigner
                  </button>
                )}

                {/* Delete button */}
                {canManage && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteSession(session); }}
                    className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                    title="Supprimer cette session"
                    data-testid={`delete-session-btn-${session.id}`}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}

                {/* Arrow */}
                <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add session modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="add-session-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
              Ajouter une session de coaching
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Élève *</label>
                <select
                  value={form.student_email}
                  onChange={(e) => setForm({ ...form, student_email: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white"
                  data-testid="add-session-student-select"
                >
                  <option value="">Sélectionner un élève...</option>
                  {students.map(s => (
                    <option key={s.id} value={s.email}>{s.first_name} {s.last_name} ({s.email})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Titre</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Session de coaching"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
                  data-testid="add-session-title-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Durée (min)</label>
                  <input
                    type="number"
                    value={form.duration_minutes}
                    onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                    placeholder="60"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
                    data-testid="add-session-duration-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Lien replay Fathom (URL)</label>
                  <input
                    type="url"
                    value={form.video_url}
                    onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                    placeholder="https://fathom.video/share/..."
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm"
                    data-testid="add-session-video-input"
                  />
                  {form.video_url && form.video_url.includes("fathom.video") && (
                    <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                      Lien Fathom détecté — résumé et transcription récupérés automatiquement
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Transcription / notes (optionnel)</label>
                <textarea
                  value={form.transcript}
                  onChange={(e) => setForm({ ...form, transcript: e.target.value })}
                  rows={4}
                  placeholder="Coller la transcription ou vos notes de session..."
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none"
                  data-testid="add-session-transcript-input"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 btn-ghost py-2.5 text-sm"
                data-testid="add-session-cancel-btn"
              >
                Annuler
              </button>
              <button
                onClick={handleAddSession}
                disabled={saving}
                className="flex-1 btn-primary py-2.5 text-sm"
                data-testid="add-session-submit-btn"
              >
                {saving ? "Ajout..." : "Ajouter"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign session modal */}
      {assignTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" data-testid="assign-session-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              Assigner cette session
            </h3>
            <p className="text-sm text-gray-500 mb-4 truncate">« {assignTarget.title} »</p>
            <label className="text-xs font-medium text-gray-600 block mb-1">Élève</label>
            <select
              value={assignEmail}
              onChange={(e) => setAssignEmail(e.target.value)}
              disabled={saving}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white"
              data-testid="assign-session-student-select"
            >
              <option value="">Sélectionner un élève...</option>
              {students.map(s => (
                <option key={s.id} value={s.email}>{s.first_name} {s.last_name} ({s.email})</option>
              ))}
            </select>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => { setAssignTarget(null); setAssignEmail(""); }}
                className="flex-1 btn-ghost py-2.5 text-sm"
                data-testid="assign-session-cancel-btn"
              >
                Annuler
              </button>
              <button
                onClick={() => handleAssignSession(assignEmail)}
                disabled={saving || !assignEmail}
                className="flex-1 btn-primary py-2.5 text-sm disabled:opacity-50"
                data-testid="assign-session-confirm-btn"
              >
                {saving ? "Assignation..." : "Confirmer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
